import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Clock, Calculator, Plug, Lock, DoorOpen, LayoutDashboard, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { useUserRole } from "@/hooks/use-user-role";
import { cn } from "@/lib/utils";
import { ReferralDialog } from "@/components/ReferralDialog";

const items = [
  { title: "Home", url: "/", icon: Home, adminOnly: false },
  { title: "My Rooms", url: "/rooms", icon: DoorOpen, adminOnly: false },
  { title: "History", url: "/history", icon: Clock, adminOnly: false },
  { title: "Calculator", url: "/calculator", icon: Calculator, adminOnly: false },
  { title: "Connectors", url: "/connectors", icon: Plug, adminOnly: false },
  { title: "Settings", url: "/settings", icon: Settings, adminOnly: false },
];

interface FlowbiteSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function FlowbiteSidebar({ collapsed = false, onToggle }: FlowbiteSidebarProps) {
  const { isAdmin, loading } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const hasAdminAccess = isAdmin;

  return (
    <aside
      data-tour-id="dashboard-nav-shell"
      className={cn(
        "h-full border-r border-border/40 bg-card/60 backdrop-blur-md transition-all duration-300 ease-in-out shrink-0",
        collapsed ? "w-[68px]" : "w-64"
      )}
    >
      <div className="h-full flex flex-col overflow-y-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className={cn(
            "flex items-center px-3 py-4 border-b border-border/30 w-full hover:bg-muted/30 transition-colors",
            collapsed ? "justify-center" : "gap-3"
          )}
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/20 text-secondary shrink-0">
            <LayoutDashboard className="h-5 w-5" />
          </div>
          {!collapsed && (
            <div className="min-w-0 text-left">
              <h1 className="font-heading text-base font-bold text-foreground truncate">Dashboard</h1>
              <p className="font-body text-xs text-muted-foreground truncate">Manage your streams</p>
            </div>
          )}
        </button>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4">
          {!collapsed && (
            <p className="px-2 mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
              Navigation
            </p>
          )}
          <ul className="space-y-1">
            {items.map((item) => {
              const isLocked = item.adminOnly && !hasAdminAccess && !loading;
              const isActive = location.pathname === item.url || 
                (item.url !== "/" && location.pathname.startsWith(item.url));

              if (isLocked) {
                return (
                  <li key={item.title}>
                    <span
                      className={cn(
                        "flex items-center rounded-lg px-3 py-2.5 text-muted-foreground/50 cursor-not-allowed",
                        collapsed && "justify-center px-0"
                      )}
                      title={collapsed ? item.title : undefined}
                    >
                      <item.icon className="h-5 w-5 shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="ms-3 flex-1 whitespace-nowrap text-lg font-medium">{item.title}</span>
                          <Lock className="h-3 w-3" />
                        </>
                      )}
                    </span>
                  </li>
                );
              }

              return (
                <li key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.url === "/"}
                    className={cn(
                      "flex items-center rounded-lg px-3 py-2.5 transition-colors duration-150 group relative",
                      collapsed && "justify-center px-0",
                      isActive
                        ? "bg-secondary/20 text-secondary font-medium"
                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                    )}
                    title={collapsed ? item.title : undefined}
                  >
                    {/* Active indicator bar */}
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-secondary rounded-r-full" />
                    )}
                    <item.icon className={cn(
                      "h-5 w-5 shrink-0 transition-colors duration-150",
                      isActive ? "text-secondary" : "group-hover:text-foreground"
                    )} />
                    {!collapsed && (
                      <span className="ms-3 whitespace-nowrap text-lg font-medium">{item.title}</span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-border/30 p-3 space-y-2">
          <ReferralDialog collapsed={collapsed} />
          <button
            onClick={onToggle}
            className={cn(
              "flex items-center justify-center w-full rounded-lg px-3 py-2 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors duration-150",
              collapsed && "px-0"
            )}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5" />
                <span className="ms-3 text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
}

// Export a standalone hook for external collapse control
// eslint-disable-next-line react-refresh/only-export-components
export function useSidebarState(defaultCollapsed = false) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const toggle = () => setCollapsed(prev => !prev);
  return { collapsed, setCollapsed, toggle };
}
